import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { RefreshCw } from "lucide-react";
import CambioCorsoModal from "@/components/CambioCorsoModal";
import VariazioneBadges from "@/components/VariazioneBadges";

const DAYS_SHORT = ["LUN", "MAR", "MER", "GIO", "VEN"];

const fmtAnno = (a) => {
  const n = Number(a);
  if (!n) return "—";
  return `${n}/${String(n + 1).slice(-2)}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const STATO_LABEL = {
  pending: { bg: "#1565c0", txt: "● PENDING" },
  confirmed: { bg: "#1b5e20", txt: "✓ APPROVATA" },
  waiting: { bg: "#ef6c00", txt: "⏳ ATTESA" },
  rejected: { bg: "#424242", txt: "✗ RIFIUTATA" },
  pagata_da_verificare: { bg: "#0d47a1", txt: "💳 DA VERIFICARE" },
  pagata: { bg: "#2e7d32", txt: "💳 PAGATA" },
  recesso: { bg: "#c62828", txt: "⊘ RECESSO" },
};

function giorniList(it) {
  const ammessi = new Set((it.corso_giorni_ammessi || []).map(Number));
  const scelti = new Set();
  if (Array.isArray(it.giorni)) {
    for (const v of it.giorni) {
      const n = Number(v);
      if (Number.isFinite(n)) scelti.add(n);
    }
  }
  // Mostra LUN-VEN, evidenziando i scelti
  return [0, 1, 2, 3, 4].map((d) => ({
    label: DAYS_SHORT[d],
    selected: scelti.has(d),
    ammesso: ammessi.size === 0 ? true : ammessi.has(d),
  }));
}

export default function AlunnoLogModal({ alunnoCod, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cambioItem, setCambioItem] = useState(null);

  const load = useCallback(async () => {
    if (!alunnoCod) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/mensa-matrice/extra-alunni/${alunnoCod}/iscrizioni`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [alunnoCod]);

  useEffect(() => { load(); }, [load]);

  if (!alunnoCod) return null;
  const a = data?.alunno || {};
  const gruppi = data?.gruppi || [];

  return (
    <>
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 5000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "30px 16px" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="alunno-log-modal"
        style={{ width: "min(960px, 100%)", background: "white", boxShadow: "0 20px 60px rgba(0,0,0,0.4)", borderRadius: 6, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(90deg,#0d47a1,#1565c0)", color: "white", padding: "14px 18px", borderRadius: "6px 6px 0 0", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 22 }}>📜</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>Storico attività EXTRA</div>
            <div style={{ fontSize: 16, fontWeight: 800 }} data-testid="alunno-log-title">
              {a.cognome ? `${a.cognome} ${a.nome}` : `Codice ${alunnoCod}`}
              <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8, opacity: 0.85 }}>Cod. {alunnoCod}</span>
            </div>
            {(a.telefono || a.email) && (
              <div style={{ fontSize: 10, marginTop: 2, opacity: 0.9 }}>
                {a.telefono && <span>📞 {a.telefono}</span>}
                {a.telefono && a.email && <span> · </span>}
                {a.email && <span>✉ {a.email}</span>}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, padding: "4px 10px", background: "rgba(255,255,255,0.18)", borderRadius: 3 }}>
            <b>{data?.totale ?? 0}</b> iscrizion{data?.totale === 1 ? "e" : "i"}
          </div>
          <button onClick={onClose} data-testid="alunno-log-close"
            style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid white", padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            ✕ Chiudi
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: 14, flex: 1 }}>
          {loading && <div style={{ padding: 20, color: "#666", fontSize: 13 }}>Caricamento storico…</div>}
          {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", fontSize: 12 }}>{err}</div>}
          {!loading && !err && gruppi.length === 0 && (
            <div style={{ padding: 20, color: "#999", textAlign: "center", fontSize: 12 }}>Nessuna iscrizione registrata.</div>
          )}
          {gruppi.map((g) => (
            <div key={g.anno} style={{ marginBottom: 18 }}>
              <div style={{ background: "#37474f", color: "white", padding: "6px 12px", fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>
                ANNO SCOLASTICO {fmtAnno(g.anno)} <span style={{ fontWeight: 400, opacity: 0.8 }}>· {g.items.length} iscrizion{g.items.length === 1 ? "e" : "i"}</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, border: "1px solid #ccc" }}>
                <thead>
                  <tr style={{ background: "#eceff1" }}>
                    <th style={{ ...th, width: 130 }}>DATA / ORA</th>
                    <th style={{ ...th, minWidth: 200 }}>CORSO</th>
                    <th style={{ ...th, width: 50, textAlign: "center" }}>CL.</th>
                    <th style={{ ...th, width: 140, textAlign: "center" }}>GIORNI</th>
                    <th style={{ ...th, width: 130, textAlign: "center" }}>STATO</th>
                    <th style={{ ...th, width: 110, textAlign: "right" }}>PREZZO €</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((it, idx) => {
                    const stCfg = STATO_LABEL[it.stato_iscrizione || (it.in_lista_attesa ? "waiting" : "pending")] || STATO_LABEL.pending;
                    const giorni = giorniList(it);
                    return (
                      <tr key={it._id || idx} style={{ borderTop: "1px solid #e0e0e0", background: idx % 2 ? "#fafafa" : "white" }}>
                        <td style={{ ...td, fontFamily: "Consolas, monospace" }}>{fmtDateTime(it.created_at)}</td>
                        <td style={td}>
                          <div style={{ fontWeight: 700 }}>{it.corso_descrizione || it.attivita || it.corso_codice}</div>
                          <div style={{ fontSize: 10, color: "#888" }}>{it.corso_codice}</div>
                        </td>
                        <td style={{ ...td, textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700 }}>{it.classe || "—"}</td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <div style={{ display: "inline-flex", gap: 2 }}>
                            {giorni.map((d) => (
                              <span key={d.label} style={{
                                padding: "1px 4px", fontSize: 9, fontWeight: 800,
                                background: d.selected ? "#1565c0" : d.ammesso ? "#eceff1" : "#fff",
                                color: d.selected ? "white" : d.ammesso ? "#37474f" : "#bbb",
                                border: d.ammesso ? "1px solid #b0bec5" : "1px solid #eee",
                                borderRadius: 2,
                              }}>{d.label}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <span style={{ padding: "2px 8px", background: stCfg.bg, color: "white", fontSize: 9, fontWeight: 700, borderRadius: 2, whiteSpace: "nowrap" }}>{stCfg.txt}</span>
                          {it.has_contabile && (
                            <div style={{ fontSize: 9, color: "#0d47a1", marginTop: 2, fontWeight: 700 }}>📎 contabile</div>
                          )}
                          {it.data_recesso && (
                            <div style={{ fontSize: 9, color: "#c62828", marginTop: 2, fontWeight: 700 }}>⊘ recesso {fmtDateTime(it.data_recesso)?.slice(0, 10)}</div>
                          )}
                          <VariazioneBadges it={it} />
                          <button onClick={() => setCambioItem({ ...it })} data-testid={`alunno-log-cambio-${it._id}`}
                                  title="Cambio corso (variazione)"
                                  style={{ marginTop: 4, background: "#6a1b9a", color: "white", border: "none", padding: "6px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                            <RefreshCw size={14} strokeWidth={2.5} /> Cambio corso
                          </button>
                        </td>
                        <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#1b5e20" }}>
                          {it.prezzo != null ? Number(it.prezzo).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
    {cambioItem && (
      <CambioCorsoModal
        isc={cambioItem}
        onClose={() => setCambioItem(null)}
        onSaved={async () => { setCambioItem(null); await load(); }}
      />
    )}
    </>
  );
}

const th = { padding: "6px 8px", textAlign: "left", fontSize: 10, fontWeight: 800, color: "#37474f", letterSpacing: 0.3, borderBottom: "2px solid #b0bec5" };
const td = { padding: "5px 8px", verticalAlign: "top" };
